import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import debounce from "lodash/debounce";
import sanitizeHtml from "sanitize-html";
import "./ProductDetails.css";

const BASE_URL =
  process.env.NODE_ENV === "development"
    ? "http://127.0.0.1:8000"
    : "https://bonobohope-chazselph.pythonanywhere.com";

const ProductDetails = ({ cart, setCart }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [frontBackImages, setFrontBackImages] = useState([]);

  const fetchProduct = debounce(async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/printify-products/${id}/`);
      if (!response.ok) throw new Error("Failed to fetch product details");
      const data = await response.json();
      const sanitizedData = {
        ...data,
        description: sanitizeHtml(data.description, {
          allowedTags: ["p", "b", "i", "ul", "li", "br"],
          allowedAttributes: {},
        }),
      };
      setProduct(sanitizedData);
      setSelectedImage(sanitizedData.images[0]?.src);

      const firstVariant =
        sanitizedData.variants.find((v) => v.is_enabled && v.is_available) ||
        sanitizedData.variants.find((v) => v.is_enabled) ||
        sanitizedData.variants[0];
      if (firstVariant) {
        const [size, color] = firstVariant.title.split(" / ");
        setSelectedSize(size || "");
        setSelectedColor(color || "");
        setSelectedVariant(firstVariant);
        const variantImages = sanitizedData.images.filter((img) =>
          img.variant_ids.includes(firstVariant.id)
        );
        setFrontBackImages(
          variantImages.length > 0
            ? variantImages.slice(0, 2)
            : sanitizedData.images.slice(0, 2)
        );
        setSelectedImage(variantImages[0]?.src || sanitizedData.images[0]?.src);
      }
      setLoading(false);
    } catch (err) {
      console.error("Fetch product error:", err);
      setError("Unable to load product details. Please try again later.");
      setLoading(false);
    }
  }, 1000);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const getVariantOptions = () => {
    const sizes = new Set();
    const colors = new Set();
    product?.variants
      .filter((v) => v.is_enabled)
      .forEach((v) => {
        const [size, color] = v.title.split(" / ");
        if (size) sizes.add(size);
        if (color) colors.add(color);
      });
    return { sizes: Array.from(sizes), colors: Array.from(colors) };
  };

  useEffect(() => {
    if (!product || !selectedSize || !selectedColor) return;
    const variant = product.variants.find(
      (v) => v.title === `${selectedSize} / ${selectedColor}` && v.is_enabled
    );
    setSelectedVariant(variant || null);
    if (variant) {
      const variantImages = product.images.filter((img) =>
        img.variant_ids.includes(variant.id)
      );
      setFrontBackImages(
        variantImages.length > 0
          ? variantImages.slice(0, 2)
          : product.images.slice(0, 2)
      );
      setSelectedImage(variantImages[0]?.src || product.images[0]?.src);
    }
  }, [selectedSize, selectedColor, product]);

  const handleAddToCart = () => {
    if (
      !selectedVariant ||
      selectedQuantity < 1 ||
      !selectedVariant.is_available
    ) {
      alert("Please select a valid, available size, color, and quantity.");
      return;
    }
    const cartItem = {
      product_id: id,
      variant_id: selectedVariant.id,
      title: `${product.title} - ${selectedSize} / ${selectedColor}`,
      quantity: selectedQuantity,
      price: selectedVariant.price / 100,
      image: selectedImage,
    };
    const existingItemIndex = cart.findIndex(
      (item) => item.variant_id === cartItem.variant_id
    );
    if (existingItemIndex > -1) {
      const updatedCart = [...cart];
      updatedCart[existingItemIndex].quantity += selectedQuantity;
      setCart(updatedCart);
    } else {
      setCart([...cart, cartItem]);
    }
    navigate("/cart");
  };

  if (loading)
    return <div className="product-details-container">Loading product...</div>;
  if (error)
    return <div className="product-details-container">Error: {error}</div>;
  if (!product)
    return <div className="product-details-container">Product not found</div>;

  const { sizes, colors } = getVariantOptions();
  const variant = selectedVariant || product.variants[0];
  const basePrice = variant ? (variant.price / 100).toFixed(2) : "N/A";
  const displayPrice =
    variant && selectedQuantity > 1
      ? `${basePrice} (x${selectedQuantity})`
      : basePrice;
  const totalPrice = variant
    ? ((variant.price / 100) * selectedQuantity).toFixed(2)
    : "N/A";
  const availabilityStatus = variant
    ? variant.is_enabled && variant.is_available
      ? "In Stock"
      : "Unavailable"
    : "Unavailable";

  return (
    <div className="product-details-container">
      <div className="product-details">
        <div className="details-image-gallery">
          <img
            className="details-main-image"
            src={selectedImage}
            alt={product.title}
          />
          {frontBackImages.length > 1 && (
            <div className="details-thumbnails">
              {frontBackImages.map((image, index) => (
                <div key={index} className="details-thumbnail-container">
                  <img
                    className={`details-thumbnail ${
                      image.src === selectedImage ? "active" : ""
                    }`}
                    src={image.src}
                    alt={`${product.title} ${image.position}`}
                    onClick={() => setSelectedImage(image.src)}
                  />
                  <span className="details-thumbnail-label">
                    {image.position}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="details-content">
          <div className="details-header">
            <h1 className="details-title">{product.title}</h1>
            <p className="details-price">${displayPrice}</p>
            {selectedQuantity > 1 && (
              <p className="total-price">Total: ${totalPrice}</p>
            )}
            <p
              className={
                availabilityStatus === "In Stock"
                  ? "details-availability in-stock"
                  : "details-availability unavailable"
              }
            >
              {availabilityStatus}
            </p>
          </div>
          <div className="variant-options">
            <div className="option-row">
              <label htmlFor="size-select">Size:</label>
              <select
                id="size-select"
                value={selectedSize}
                onChange={(e) => setSelectedSize(e.target.value)}
              >
                <option value="">Select Size</option>
                {sizes.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            <div className="option-row">
              <label htmlFor="color-select">Color:</label>
              <select
                id="color-select"
                value={selectedColor}
                onChange={(e) => setSelectedColor(e.target.value)}
              >
                <option value="">Select Color</option>
                {colors.map((color) => (
                  <option key={color} value={color}>
                    {color}
                  </option>
                ))}
              </select>
            </div>
            <div className="option-row">
              <label htmlFor="quantity-select">Quantity:</label>
              <select
                id="quantity-select"
                value={selectedQuantity}
                onChange={(e) => setSelectedQuantity(parseInt(e.target.value))}
              >
                {[1, 2, 3, 4, 5].map((qty) => (
                  <option key={qty} value={qty}>
                    {qty}
                  </option>
                ))}
              </select>
            </div>
            <div className="option-row">
              <button
                className="add-to-cart-btn"
                onClick={handleAddToCart}
                disabled={!selectedVariant || !selectedVariant.is_available}
              >
                Add to Cart
              </button>
            </div>
          </div>
          <div className="product-details-section">
            <h2>Product Details</h2>
            <div
              className="details-description"
              dangerouslySetInnerHTML={{ __html: product.description }}
            />
            <div className="return-policy">
              <h2>Return Policy</h2>
              <p>
                We want you to love your purchase! Returns are accepted within
                30 days of delivery for defective, damaged, or incorrect items.
                Please email{" "}
                <a href="mailto:info@bonobohope.org">info@bonobohope.org</a>{" "}
                with your order details and photos (if applicable) to initiate a
                return or replacement. Note that all print-on-demand items, including
                out T-shirts, posters, and coffee mugs, are
                non-returnable unless defective.  Return requests for these items
                must include photos.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
